/*

  Riddle.ts

  Gáta Dagsins game class - daily puzzle game

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { BaseGame } from "./basegame";
import { wordChecker } from "./wordcheck";
import { request } from "./request";
import {
  ServerRiddle, IRiddle, IModel,
  RiddleWord, RACK_SIZE, RackTile,
  BestScore, IPlayerMove,
} from "./types";
import { coord, toVector } from "./util";
import { RiddlePersistence } from "./riddlePersistence";

const HOT_WARM_BOUNDARY_RATIO = 0.5;
const WARM_COLD_BOUNDARY_RATIO = 0.25;

export class Riddle extends BaseGame implements IRiddle {
  // Daily puzzle properties
  date: string; // Format: YYYY-MM-DD
  model: IModel;

  // Scoring properties, static
  bestPossibleScore: number = 0;
  warmBoundary: number = 0;
  hotBoundary: number = 0;

  // Scoring properties, dynamic
  globalBestScore: BestScore | null = null;
  groupBestScore: BestScore | null = null;
  personalBestScore: number = 0;
  playerMoves: RiddleWord[] = [];

  constructor(uuid: string, date: string, model: IModel) {
    if (!model.state) {
      throw new Error("No global state in Riddle constructor");
    }
    super(uuid, model.state);
    this.date = date;
    this.model = model;
  }

  buttonState(): Record<string, boolean> {
    // Simplified button state for Gáta Dagsins
    let s: Record<string, boolean> = {};
    s.tilesPlaced = this.tilesPlaced().length > 0;
    s.gameOver = false;
    s.localTurn = true; // Always the player's turn in single-player
    s.showingDialog = this.showingDialog !== null;
    s.wordGood = this.wordGood;
    s.wordBad = this.wordBad;
    s.canPlay = this.wordGood && s.tilesPlaced;
    s.showRecall = s.tilesPlaced;
    s.showScramble = !s.tilesPlaced;
    s.showMove = false; // No traditional move submission
    s.showMoveMobile = false;
    s.showPass = false;
    s.showExchange = false;
    s.showResign = false;
    s.showChallenge = false;
    s.showChallengeInfo = false;
    s.showForceResignMobile = false;
    return s;
  }

  allowDragDrop(): boolean {
    return !this.showingDialog;
  }

  async load(date: string, locale: string): Promise<void> {
    this.date = date;
    this.locale = locale;
    const { state } = this;

    try {
      if (!state) throw new Error("No global state in Riddle.load");
      // Request riddle data from server (HTTP API call)
      const response = await request<{
        ok: boolean;
        riddle: ServerRiddle;
      }>(state, {
        method: "POST",
        url: "/gatadagsins/riddle",
        body: { date, locale }
      });
      
      if (response.ok) {
        this.board_type = response.riddle.board_type || "standard";
        this.alphabet = response.riddle.alphabet || "";
        this.tile_scores = response.riddle.tile_scores || {};
        this.two_letter_words = response.riddle.two_letter_words || [];
        this.initBoard(response.riddle.board || []);
        this.initRack(response.riddle.rack || []);
        this.bestPossibleScore = response.riddle.max_score || 0;
        this.globalBestScore = null;
        this.groupBestScore = null;
        this.personalBestScore = 0;
        this.warmBoundary = this.bestPossibleScore * WARM_COLD_BOUNDARY_RATIO;
        this.hotBoundary = this.bestPossibleScore * HOT_WARM_BOUNDARY_RATIO;
        // Initialize word checker
        wordChecker.ingestTwoLetterWords(this.locale, this.two_letter_words[0]);

        // Load persisted player moves from localStorage
        if (state.netskraflUserId) {
          const persistedMoves = RiddlePersistence.loadLocalMoves(state.netskraflUserId, date);
          if (persistedMoves.length > 0) {
            // Convert from IPlayerMove to RiddleWord format, preserving timestamps
            this.playerMoves = persistedMoves.map(move => ({
              word: move.word,
              score: move.score,
              coord: move.coord,
              timestamp: move.timestamp || new Date().toISOString() // Use stored timestamp or fallback
            }));

            // Update personal best score from persisted moves
            const bestMove = persistedMoves.reduce((best, current) =>
              current.score > best.score ? current : best
            );
            this.personalBestScore = bestMove.score;
          }
        }
      }
    } catch (error) {
      console.error('Failed to load daily riddle:', error);
      this.currentError = "Gat ekki lesið gátu dagsins. Reyndu aftur síðar.";
    }
  }

  async submitRiddleWord(move: RiddleWord): Promise<void> {
    const { state } = this;
    if (!state || !state.netskraflUserId) return;

    try {
      await request(state, {
        method: "POST",
        url: "/gatadagsins/submit",
        body: {
          date: this.date,
          locale: this.locale,
          userId: state.netskraflUserId,
          groupId: state.userGroupId || null,
          userDisplayName: state.userFullname || state.userNick || "",
          move,
        }
      });
    } catch (error) {
      console.error('Failed to submit move:', error);
      this.currentError = "Netþjónn gat ekki tekið á móti lögninni.";
    }
  }

  addToPlayerMoves(timestamp: string): RiddleWord | undefined {
    // Add the current move, assumed to be valid,
    // to the player's moves history
    if (!this.wordGood
      || !this.currentScore
      || !this.currentWordWithBlanks
      || !this.currentCoord
    ) {
      return;
    }
    // Check whether the move already exists
    const move: RiddleWord = {
      score: this.currentScore,
      word: this.currentWordWithBlanks,
      coord: this.currentCoord,
      timestamp,
    };
    const existingMove = this.playerMoves.find(m =>
      m.word === move.word &&
      m.coord === move.coord &&
      m.score === move.score
    );
    if (existingMove) {
      // If the move already exists, do not add it again
      return;
    }
    // Add the move to the player's moves history
    // with the current timestamp. Note that the move list
    // does not need to be sorted; they are sorted by score
    // when displayed in the UI (i.e. in the thermometer).
    this.playerMoves.push(move);
    return move;
  }

  async updateScore() {
    await super.updateScore();
    // Update the best scores after a move
    // Attempt to add this move to player moves
    const timestamp = new Date().toISOString();
    const move = this.addToPlayerMoves(timestamp);
    // If the move is not valid or was already played, return
    if (!move) return;

    const { state } = this;
    if (!state || !state.netskraflUserId) return;

    // Save all moves to localStorage (local backup/cache)
    // Convert RiddleWord[] to IPlayerMove[] for persistence
    const movesToSave: IPlayerMove[] = this.playerMoves.map(m => ({
      score: m.score,
      word: m.word,
      coord: m.coord,
      timestamp: m.timestamp
    }));
    RiddlePersistence.saveLocalMoves(state.netskraflUserId, this.date, movesToSave);

    // If the move does not improve the personal best, we're done
    if (move.score <= this.personalBestScore) return;

    // This is the best score we've seen yet
    this.personalBestScore = move.score;
    // Submit to server; the server handles all Firebase updates
    // (achievements, stats, global best, leaderboard)
    this.submitRiddleWord(move);
  }

  updateGlobalBestScore (best: BestScore) {
    // Update the global best score, typically as a result
    // of a Firebase notification from the server
    this.globalBestScore = best;
  }

  updateGroupBestScore (best: BestScore) {
    // Update the group best score, typically as a result
    // of a Firebase notification from the server
    this.groupBestScore = best;
  }

  recreateWordOnBoard(word: string, startCoord: string) {
    // Recreate a word on the board from rack tiles, using the same logic as calcScore
    // but in reverse - placing tiles to form the word
    // The word parameter contains question marks before letters that were played with blank tiles
    
    // First, recall all tiles to the rack
    this._resetRack();
    
    // Parse the coordinate using toVector to get row, column, and direction
    const { row, col, dx, dy } = toVector(startCoord);
    
    // Find available rack tiles
    const availableRackTiles: string[] = [];
    for (let i = 1; i <= RACK_SIZE; i++) {
      const co = `R${i}`;
      if (co in this.tiles) {
        availableRackTiles.push(co);
      }
    }

    // Place tiles to form the word, snaking through existing tiles
    let currentRow = row;
    let currentCol = col;
    
    // Parse the word to identify blank tiles (marked with ?)
    let wordIndex = 0;
    while (wordIndex < word.length) {
      let targetLetter: string;
      let isBlankTile = false;
      
      if (word[wordIndex] === '?') {
        // This is a blank tile - next character is the letter it represents
        isBlankTile = true;
        wordIndex++; // Skip the '?'
        targetLetter = word[wordIndex];
      } else {
        targetLetter = word[wordIndex];
      }
      
      const targetCoord = coord(currentRow, currentCol);
      
      if (!targetCoord) {
        // Invalid board position
        return;
      }
      
      const existingTile = this.tileAt(currentRow, currentCol);
      
      if (existingTile && !existingTile.draggable) {
        // There's already a fixed tile here - it should match the word
        if (existingTile.letter !== targetLetter) {
          // Word doesn't match existing tiles - can't recreate
          return;
        }
        // Skip this position, the tile is already there
      } else {
        // Need to place a tile here from the rack
        if (availableRackTiles.length === 0) {
          // No more rack tiles available
          return;
        }
        
        // Find a matching tile in the rack
        let foundTile = false;
        for (let j = 0; j < availableRackTiles.length; j++) {
          const rackTileCoord = availableRackTiles[j];
          const rackTile = this.tiles[rackTileCoord];

          if (rackTile) {
            const rackLetter = rackTile.tile === '?' ? ' ' : rackTile.tile;

            if (isBlankTile) {
              // We need a blank tile specifically
              if (rackTile.tile === '?') {
                // Found a blank tile - set its letter
                rackTile.letter = targetLetter;
                // Move the tile from rack to board
                this.tiles[targetCoord] = rackTile;
                delete this.tiles[rackTileCoord];
                // Remove this tile from available list
                availableRackTiles.splice(j, 1);
                foundTile = true;
                break;
              }
            } else {
              // We need a regular tile
              if (rackLetter === targetLetter) {
                // Found matching tile - move it to the board
                this.tiles[targetCoord] = rackTile;
                delete this.tiles[rackTileCoord];
                // Remove this tile from available list
                availableRackTiles.splice(j, 1);
                foundTile = true;
                break;
              }
            }
          }
        }
        
        if (!foundTile) {
          // Couldn't find a matching tile in the rack
          return;
        }
      }
      
      // Move to next position
      currentRow += dy;
      currentCol += dx;
      wordIndex++;
    }
    
    // Update the score to reflect the new word
    this.updateScore();
    this.saveTiles();
  }

  private initRack(rack: RackTile[]) {
    // Clear existing rack tiles
    for (let i = 1; i <= RACK_SIZE; i++) {
      delete this.tiles[`R${i}`];
    }
    // Add new rack tiles
    for (let i = 0; i < rack.length && i < RACK_SIZE; i++) {
      const [tile, score] = rack[i];
      this.tiles[`R${i + 1}`] = {
        player: 0,
        tile: tile,
        letter: tile === "?" ? " " : tile,
        score: score,
        freshtile: true,
        draggable: true,
        index: i,
        xchg: false
      };
    }
  }

  private initBoard(board: string[]) {
    // Initialize the tiles object from a list of 15 strings,
    // with each string containing 15 characters corresponding
    // to the board columns. Characters in uppercase denote
    // blank tiles that have the meaning of the corresponding
    // lowercase letter, e.g. "A" is a blank tile with the meaning "a".
    this.tiles = {};
    for (let i = 0; i < 15; i++) {
      const row = board[i] || "";
      for (let j = 0; j < 15; j++) {
        const tile = row[j] || "";
        const co = coord(i, j);
        if (!tile || !co) continue; // Skip empty tiles or invalid coordinates
        const lowercaseTile = tile.toLowerCase();
        const isBlank = lowercaseTile !== tile;
        if (this.alphabet.includes(lowercaseTile)) {
          const score = isBlank ? 0 : (this.tile_scores[lowercaseTile] || 1);
          this.tiles[co] = {
            player: 0,
            tile: isBlank ? "?" : lowercaseTile,
            letter: lowercaseTile,
            score,
            freshtile: false,
            draggable: false,
            index: -1, // Not applicable for board tiles
            xchg: false
          };
        }
      }
    }
  }

  cleanup() {
    // Firebase listeners will be cleaned up by the Actions layer
    // when cleanupGataDagsinsGame is called
    super.cleanup();
  }
}
